import { Subject } from 'rxjs';
import { AuctionDTO, ChecklistItemStatusDTO, ChecklistItemStatusHistoryDTO, StatusChangeReasonDTO } from "../DTO/auctionDTO";
import { AuctionBid } from './auctionBid';
import { BidPublishingData } from './bidPublishingData';
import { AuctionStatusPublishingData } from './auctionStatusPublishingData';
import { AuctionSchedule } from './auctionSchedule';
import { PropertyExpense } from './propertyExpense';

export class Auction{
    public id!:number;
    public auctionType?:number; //1=Regular Auction, 2=Make Offer/Buy Now, 3=For Sale, 4=Call For Offers
    public startPrice?:number; //also somtimes called minimumBid
    public endPrice?:number;
    public reservePrice?:number;
    public reserveMet:boolean = false; //this takes reserveCredit into consideration for public view, but not for admin view
    public buyNowPrice?:number;
    public amountToAuthorize?:number;
    public buyerPremium?:number;
    public deposit?:number;
    public depositValue?:number;
    public securedPrivateAuction:boolean = false;
    public privateBids:boolean = false;
    public feature:boolean = false;
    public comingSoon:boolean = false;
    public schedule:AuctionSchedule[]; 
    public scheduleSeqNo?:number; //only needed for auction type 1 (auction)
    public auctionNonComplianceReasons?:string;
    public legalAdId?:number;
    public legalAdConfirmed:boolean = false;
    public auctionStatus!:number; //0=no auction, 1=ready for auction, 3=sale pending, 5=auction ended without winner, 6=auction ended with winner, 7=sale closed, 9=cancelled
    public bids?:AuctionBid[];
    /** the logged in user's bidder key for this auction: public bids carry bidder keys instead of user ids (see AuctionService.getAuctionBidHistory) */
    public ownBidderKey?:string;
    public auctionIsLive:boolean = false;
    public auctionAutoLive:boolean = false;
    public increment?:number;
    public requiresNDA:boolean = false;
    public hasBackupBuyNow:boolean = false;
    public checklistItemsStatus?:ChecklistItemStatus[];
    public auctionIsPostponed:boolean = false;
    public auctionIsCancelled:boolean = false;
    public auctionIsClosed:boolean = false;
    public statusChangeReasons?:StatusChangeReason[];
    public purchaseAgreementTemplateId?:number;
    public buyerUploadsRequired?:BuyerUploadRequired[];
    public showReserveMet:boolean = false;
    public leads?:Lead[];
    public saleStatus?:number; //internal only (admins, client users), non auction sale procedures: 0=For Sale, 10=Pending, 20=Under Contract; not related to auctionStatus 3 (sale pending). undefined = not part of the data (public reads)

    public static saleStatusOptions = [{id:0,name:'For Sale'},{id:10,name:'Pending'},{id:20,name:'Under Contract'}];

    get saleStatusLabel(){
        return Auction.saleStatusOptions.find(o=>o.id===this.saleStatus)?.name;
    }

    //sale status is shown for non auction sale procedures until the listing is closed or cancelled
    get showSaleStatus():boolean{
        return this.auctionType!=1 && (this.saleStatus ?? 0)>0 && !this.auctionIsClosed && !this.auctionIsCancelled;
    }

    public static getAuctionStatusString(id:number){
        switch(id){
            case 0:
                return "No Auction";
            case 1:
                return "Ready For Auction";
            case 3:
                return "Sale Pending";
            case 5:
                return "Ended without winner";
            case 6:
                return "Ended with winner";
            case 7:
                return "Sale closed";
            case 9:
                return "Cancelled";
            default:
                return "unknown"
        }
    }
    

    get auctionIsLiveOrCancelledWithinSchedule(){
        return this.auctionIsLive || (this.auctionIsCancelled && this.auctionWithinSchedule)
    }

    get auctionWithinSchedule(){
        if(!this.auctionStarting || !this.auctionEnding) return false;
        const now = new Date();
        return now >= new Date(this.auctionStarting) && now <= new Date(this.auctionEnding);
    }

    set suspendBidsSeconds(value:number){
        if(value){
            var t= new Date();
            this.suspendBidsEnding=new Date(t.getTime()+value*1000);
        }
    }
    get suspendBidsSeconds():number{
        var sec=0;
        if(this.suspendBidsEnding){
            var t=new Date();
            var diff=this.suspendBidsEnding.getTime()-t.getTime();
            sec=(diff>0)?diff/1000:0;
        }
        return sec;
    }

    set suspendBuyNowSeconds(value:number){
        if(value){
            var t= new Date();
            this.suspendBuyNowEnding=new Date(t.getTime()+value*1000);
        }
    }

    get suspendBuyNowSeconds():number{
        var sec=0;
        if(this.suspendBuyNowEnding){
            var t=new Date();
            var diff=this.suspendBuyNowEnding.getTime()-t.getTime();
            sec=(diff>0)?diff/1000:0;
        }
        return sec;
    }

    set salePendingSeconds(value:number){
        if(value){
            var t= new Date();
            this.salePendingEnding=new Date(t.getTime()+value*1000);
        }
    }
    get salePendingSeconds():number{
        var sec=0;
        if(this.salePendingEnding){
            var t=new Date();
            var diff=this.salePendingEnding.getTime()-t.getTime();
            sec=(diff>0)?diff/1000:0;
        }
        return sec;
    }

    get usersBids():AuctionBid[]|undefined{
        return this.bids?.filter(b=>b.requestorBid==true);
    }

    public suspendBidsEnding?:Date;
    public suspendBuyNowEnding?:Date;
    public salePendingEnding?:Date

    public auctionUpdatedSubject = new Subject();

    public bpSpreadData:BPSpreadData;

    constructor(){ 
        this.schedule=[];
        this.bids=[];
        this.statusChangeReasons=[];
        this.bpSpreadData=new BPSpreadData(this);
    }

    get currentSchedule(){
        return this.schedule.find(s=>s.seqNo==this.scheduleSeqNo);
    }

    get highestBidAmount():number{
        let highestBid=0;
        //make sure bid at index 0 is always the highest bid
        if(this.bids && this.bids.length>0){
           highestBid=this.bids[0].currentBid;
        }
        return highestBid;
    }

    get minimumBid():number{
        if(this.highestBidAmount>0 && this.increment){
            return Math.ceil(this.highestBidAmount)+this.increment; 
        }       
        else {
            return this.noMinimumBid==true?0:Math.ceil(this.startPrice ?? 0);
        }
    }

    get hasBids(){
        return this.bids && this.bids.length>0;
    }

    get currentPrice():number | undefined{
        if(this.auctionType==1){
            if(this.hasBids){
                return this.bids![0].currentBid;
            }
            else return this.startPrice;
        }
        else if(this.auctionType==2){
            if(this.hasBids){
                let latestsAskBid:AuctionBid|undefined = undefined;
                for (let b of this.bids!){
                    if(b.askAmount && b.askAmountPublic) {
                        latestsAskBid=b;
                        break;
                    }
                }
                if(latestsAskBid) return latestsAskBid.askAmount
                else return this.buyNowPrice
            }
            else return this.buyNowPrice;
        }
        else return undefined;
    }

    get currentDisplayPrice():number | undefined{
        if(!this.currentPrice) return undefined;
        return Math.ceil(this.currentPrice);
    }

    get noMinimumBid():boolean|undefined{
        return this.currentSchedule?.noMinimumBid;
    }

    get auctionStarting():Date|undefined{
        return this.currentSchedule?.auctionStarting;
    }

    get auctionEnding():Date|undefined{
        return this.currentSchedule?.auctionEnding;
    }

    set auctionEnding(value:Date){
        if(this.currentSchedule) this.currentSchedule.auctionEnding=value;
    }

    get auctionEndingUnix():number|undefined{
        return this.currentSchedule?.auctionEnding?.getTime();
    }

    get auctionEnded():boolean|undefined{
        return this.auctionType!=3 && this.currentSchedule?.auctionEnded;
    }
    set auctionEnded(value:boolean){
        if(this.currentSchedule) this.currentSchedule.scheduleEnded=value;
    }

    get auctionWasNeverLive():boolean|undefined{
        return this.currentSchedule?.wasNeverLive;
    }

    get previousAuctionEnding():Date|undefined{
        return this.currentSchedule?.previousAuctionEnding;
    }

    get suspendBidsTimer():number{
        if(this.suspendBidsEnding){
            let seconds=Math.round((this.suspendBidsEnding.getTime()-(new Date()).getTime())/1000);
            return (seconds>0)?seconds:0;
        }
        return 0;
    }

    get suspendBidsMinutes():number{
        return Math.ceil(this.suspendBidsTimer/60);
    }

    get suspendBuyNowTimer():number{
        if(this.suspendBuyNowEnding){
            let seconds=Math.round((this.suspendBuyNowEnding.getTime()-(new Date()).getTime())/1000);
            return (seconds>0)?seconds:0;
        }
        return 0;
    }

    get suspendBuyNowMinutes():number{
        return Math.ceil(this.suspendBuyNowTimer/60);
    }

    get salePendingTimer():number{
        if(this.salePendingEnding){
            let seconds=Math.round((this.salePendingEnding.getTime()-(new Date()).getTime())/1000);
            return (seconds>0)?seconds:0;
        }
        return 0;
    }

    get isSalePending():boolean{
        return this.auctionIsLive && (this.auctionStatus==3 || this.salePendingTimer>0);
    }

    get isNegotiating():boolean{
        return this.auctionIsLive && this.suspendBuyNowTimer>0;
    }

    get canBuyNow():boolean{
        return this.auctionIsLive && !this.isNegotiating && !this.isSalePending;
    }

    get canPlaceBid():boolean{
        return this.auctionIsLive && this.suspendBidsTimer==0 && !this.isSalePending && !this.isNegotiating;
    }

    get canBackupBuyNow():boolean{
        return this.isSalePending;
    }

    get canGoLive():boolean{
        return this.auctionNonComplianceReasons==undefined || this.auctionNonComplianceReasons.length==0;
    }

    get showFeatured():boolean{
        return this.feature && !this.auctionEnded;
    }

    get showComingSoon():boolean{
        return this.comingSoon && !this.auctionEnded;
    }

    get reserveCredit():number|undefined{
        return this.currentSchedule?.reserveCredit;
    }

    set reserveCredit(value:number | undefined){
        if(this.currentSchedule) this.currentSchedule.reserveCredit=value;
    }

    get reserveMetWithCredit(){
        if(!this.reserveCredit || !this.reservePrice || !this.currentPrice) return false;
        const rc = this.reserveCredit??0;
        return this.reservePrice-rc<=this.currentPrice; 
    }

    get displayBidCount(){
        if(this.bids){
            if(this.bids.length==1) return '1 Bid';
            else return this.bids.length+' Bids';
        }
        else return '0 Bids';
    }

    public updateAuction(bpd:BidPublishingData, userId:string|undefined){
        if(this.auctionType==1){
            this.reserveMet=bpd.reserveMet;
            var schedule = this.schedule.find(s=>s.seqNo==bpd.scheduleSeqNo)!;
            if(bpd.auctionEndTime) schedule.auctionEnding=new Date(bpd.auctionEndTime);
            if(!this.bids) this.bids=new Array<AuctionBid>();
            //bid needs to be put in place where the highest is at the beginning, but in case of same amount with current high, the curent high stays on top
            //in cases where an autobid kicked in we will also have a 'triggerAuctionBid' object which needs to be processed first
            if(bpd.triggerAuctionBid) this.bids.unshift(AuctionBid.mapFromDTO(bpd.triggerAuctionBid, userId));
            if(bpd.auctionBid){
                this.bids.unshift(AuctionBid.mapFromDTO(bpd.auctionBid, userId));
                this.bids.sort((bidA,bidB)=>{return AuctionBid.compare(bidA,bidB);});
                //bid ranking is updated by the caller of this method.
            }
        }
        else if(this.auctionType==2){
            this.suspendBidsSeconds=bpd.suspendBidsSeconds!;
            this.suspendBuyNowSeconds=bpd.suspendBuyNowSeconds!;
            this.salePendingSeconds=bpd.salePendingSeconds!;
            if(bpd.auctionStatus)this.auctionStatus=bpd.auctionStatus; //as soon as the buyer fills out contract during salePendingSeconds, the timers will be reset and the auctionStatus will become 3; now the contract is with the auction admin for review and execution. Only an auction admin can remove status 3 from auction and put it back in sales process.
            if(bpd.auctionBid) {
                if(!this.bids) this.bids=new Array<AuctionBid>();
                //bid needs to be put in place where the highest is at the beginning, but in case of same amount with current high, the curent high stays on top
                this.bids.unshift(AuctionBid.mapFromDTO(bpd.auctionBid, userId));
                this.bids.sort((bidA,bidB)=>{return AuctionBid.compare(bidA,bidB);})
                //bid ranking is updated by the caller of this method.
            }
        }
        this.auctionUpdatedSubject.next(undefined);
    }

    public updateAuctionBidRanking(idArray:number[]){
        if(this.bids){
            this.bids.forEach(bid=> bid.rank=undefined);
            let rank=1;
            idArray.forEach(id => {
                let auctionBid = this.bids!.find(bid => bid.id==id)
                if(auctionBid) {
                auctionBid.rank=rank;
                rank++;
                }
            });
            this.auctionUpdatedSubject.next(undefined);
        }
    }

    public updateAuctionStatus(spd:AuctionStatusPublishingData){
        if(this.auctionType==1){
            if(spd.auctionStatus) this.auctionStatus=spd.auctionStatus;
        }
        else if(this.auctionType==2){
            if(spd.suspendBidsSeconds) this.suspendBidsSeconds=spd.suspendBidsSeconds;
            if(spd.suspendBuyNowSeconds) this.suspendBuyNowSeconds=spd.suspendBuyNowSeconds;
            if(spd.salePendingSeconds) this.salePendingSeconds=spd.salePendingSeconds;
            if(spd.auctionStatus) this.auctionStatus=spd.auctionStatus; 
        }
        if(spd.auctionStatus==9) this.auctionIsCancelled=true;
        this.auctionUpdatedSubject.next(undefined);
    }

    public updateAuctionStarted(spd:AuctionStatusPublishingData){
        if(spd.auctionStatus) this.auctionStatus=spd.auctionStatus;
        this.auctionIsLive=true;
        this.auctionUpdatedSubject.next(undefined);
    }

    public updateAuctionEnded(spd:AuctionStatusPublishingData){
        if(spd.auctionStatus) this.auctionStatus=spd.auctionStatus;
        this.auctionIsLive=false;
        this.auctionEnded=true;
        this.auctionUpdatedSubject.next(undefined);
    }

    public updateAuctionEndPostponed(spd:AuctionStatusPublishingData){
        if(this.auctionType==1 && spd.auctionEndTime){
            this.auctionEnding=new Date(spd.auctionEndTime);
        }
        else if(this.auctionType==2){
           //currently we only receive this for auctiontype 1
        }
        this.auctionUpdatedSubject.next(undefined);
    }
  
    public static mapToDTO(param:Auction) : AuctionDTO{
        
        let dto = new AuctionDTO();
        try
        {
            dto.id = param.id;
            dto.auctionType = param.auctionType
            dto.startPrice = param.startPrice;
            dto.endPrice = param.endPrice;
            dto.reservePrice = param.reservePrice;
            dto.reserveMet = param.reserveMet;
            dto.buyNowPrice = param.buyNowPrice;
            dto.amountToAuthorize = param.amountToAuthorize;
            dto.buyerPremium = param.buyerPremium;
            dto.deposit = param.deposit;
            dto.depositValue = param.depositValue;
            dto.securedPrivateAuction = param.securedPrivateAuction;
            dto.privateBids = param.privateBids;
            dto.feature = param.feature;
            dto.comingSoon = param.comingSoon;
            if(param.schedule) dto.schedule = param.schedule.map(as=>AuctionSchedule.mapToDTO(as));
            dto.scheduleSeqNo = param.scheduleSeqNo;
            dto.auctionNonComplianceReasons = param.auctionNonComplianceReasons;
            dto.legalAdId = param.legalAdId
            dto.legalAdConfirmed = param.legalAdConfirmed;
            dto.auctionStatus = param.auctionStatus;
            if(param.bids) dto.bids = param.bids.map(b=>AuctionBid.mapToDTO(b));
            dto.auctionIsLive = param.auctionIsLive;
            dto.auctionAutoLive = param.auctionAutoLive;
            dto.increment = param.increment;
            dto.requiresNDA = param.requiresNDA;
            dto.hasBackupBuyNow = param.hasBackupBuyNow;
            dto.suspendBidsSeconds = param.suspendBidsSeconds;
            dto.suspendBuyNowSeconds = param.suspendBuyNowSeconds;
            dto.salePendingSeconds = param.salePendingSeconds;
            if(param.checklistItemsStatus) dto.checklistItemsStatus = JSON.stringify(param.checklistItemsStatus.map(clis=>ChecklistItemStatus.mapToDTO(clis)));
            dto.auctionIsPostponed = param.auctionIsPostponed;
            dto.auctionIsCancelled = param.auctionIsCancelled;
            dto.auctionIsClosed = param.auctionIsClosed;
            if(param.statusChangeReasons) dto.statusChangeReasons = param.statusChangeReasons.map(scr=>StatusChangeReason.mapToDTO(scr));
            dto.purchaseAgreementTemplateId = param.purchaseAgreementTemplateId;
            dto.buyerUploadsRequired = JSON.stringify(param.buyerUploadsRequired);
            dto.showReserveMet = param.showReserveMet;
            if(param.leads) dto.leads = JSON.stringify(param.leads);
            dto.saleStatus = param.saleStatus;
        }
        catch(e)
        {
            console.log(e);
        }
        return dto;
    }
    public static mapFromDTO(param:AuctionDTO, userId:string|undefined) : Auction
    {
        
        let obj = new Auction();
        try
        {
            obj.id = param.id;
            obj.auctionType = param.auctionType;
            obj.startPrice = param.startPrice;
            obj.endPrice = param.endPrice;
            obj.reservePrice = param.reservePrice;
            obj.reserveMet = param.reserveMet;
            obj.buyNowPrice = param.buyNowPrice;
            obj.amountToAuthorize = param.amountToAuthorize;
            obj.buyerPremium = param.buyerPremium;
            obj.deposit = param.deposit;
            obj.depositValue = param.depositValue;
            obj.securedPrivateAuction = param.securedPrivateAuction;
            obj.privateBids = param.privateBids;
            obj.feature = param.feature;
            obj.comingSoon = param.comingSoon;
            if(param.schedule) obj.schedule = param.schedule.map(as=>AuctionSchedule.mapFromDTO(as));
            obj.scheduleSeqNo = param.scheduleSeqNo;
            obj.auctionNonComplianceReasons = param.auctionNonComplianceReasons;
            obj.legalAdId = param.legalAdId;
            obj.legalAdConfirmed = param.legalAdConfirmed;
            obj.auctionStatus = param.auctionStatus;
            if(param.bids) obj.bids = param.bids.map(b=>AuctionBid.mapFromDTO(b, userId));
            obj.auctionIsLive = param.auctionIsLive;
            obj.auctionAutoLive = param.auctionAutoLive;
            obj.increment = param.increment;
            obj.requiresNDA = param.requiresNDA;
            obj.hasBackupBuyNow = param.hasBackupBuyNow;
            obj.suspendBidsSeconds = param.suspendBidsSeconds;
            obj.suspendBuyNowSeconds = param.suspendBuyNowSeconds;
            obj.salePendingSeconds = param.salePendingSeconds;
            if(param.checklistItemsStatus){
                obj.checklistItemsStatus=[];
                let clisArray:ChecklistItemStatusDTO[] = JSON.parse(param.checklistItemsStatus);
                clisArray.forEach(clis=>{
                    obj.checklistItemsStatus!.push(ChecklistItemStatus.mapFromDTO(clis));
                });
            };
            obj.auctionIsPostponed = param.auctionIsPostponed;
            obj.auctionIsCancelled = param.auctionIsCancelled;
            obj.auctionIsClosed = param.auctionIsClosed;
            obj.purchaseAgreementTemplateId = param.purchaseAgreementTemplateId;
            if(param.statusChangeReasons) obj.statusChangeReasons = param.statusChangeReasons.map(scr=>StatusChangeReason.mapFromDTO(scr));
            if(param.buyerUploadsRequired) {
                obj.buyerUploadsRequired=[];
                const burArray = JSON.parse(param.buyerUploadsRequired);
                burArray.forEach((bur:any) => {
                    obj.buyerUploadsRequired!.push(Object.assign(new BuyerUploadRequired(),bur));
                });
            }
            obj.showReserveMet = param.showReserveMet;
            obj.saleStatus = param.saleStatus ?? undefined; //public reads deliver null
            if(param.leads){
                obj.leads=[];
                const leadsArray = JSON.parse(param.leads);
                leadsArray.forEach((lead:any) => {
                    obj.leads!.push(Object.assign(new Lead(),lead));
                });
            }
        }
        catch(e)
        {
            console.log(e);
        }
        return obj;
    }
    
    public static updateFromDTO(obj:Auction, param:AuctionDTO, userId:string)
    {
        try
        {
            obj.id = param.id;
            obj.auctionType = param.auctionType;
            obj.startPrice = param.startPrice;
            obj.endPrice = param.endPrice;
            obj.reservePrice = param.reservePrice;
            obj.reserveMet = param.reserveMet;
            obj.buyNowPrice = param.buyNowPrice;
            obj.amountToAuthorize = param.amountToAuthorize;
            obj.buyerPremium = param.buyerPremium;
            obj.deposit = param.deposit;
            obj.depositValue = param.depositValue;
            obj.securedPrivateAuction = param.securedPrivateAuction;
            obj.privateBids = param.privateBids;
            obj.feature = param.feature;
            obj.comingSoon = param.comingSoon;
            if(param.schedule) obj.schedule = param.schedule.map(as=>AuctionSchedule.mapFromDTO(as));
            obj.scheduleSeqNo = param.scheduleSeqNo;
            obj.auctionNonComplianceReasons = param.auctionNonComplianceReasons;
            obj.legalAdId = param.legalAdId;
            obj.legalAdConfirmed = param.legalAdConfirmed;
            obj.auctionStatus = param.auctionStatus;
            if(param.bids) obj.bids = param.bids.map(b=>AuctionBid.mapFromDTO(b, userId));
            obj.auctionIsLive = param.auctionIsLive;
            obj.auctionAutoLive = param.auctionAutoLive;
            obj.increment = param.increment;
            obj.requiresNDA = param.requiresNDA;
            obj.hasBackupBuyNow = param.hasBackupBuyNow;
            obj.suspendBidsSeconds = param.suspendBidsSeconds;
            obj.suspendBuyNowSeconds = param.suspendBuyNowSeconds;
            obj.salePendingSeconds = param.salePendingSeconds;
            if(param.checklistItemsStatus){
                obj.checklistItemsStatus=[];
                let clisArray:ChecklistItemStatusDTO[] = JSON.parse(param.checklistItemsStatus);
                clisArray.forEach(clis=>{
                    obj.checklistItemsStatus!.push(ChecklistItemStatus.mapFromDTO(clis));
                });
            };
            obj.auctionIsPostponed = param.auctionIsPostponed;
            obj.auctionIsCancelled = param.auctionIsCancelled;
            obj.auctionIsClosed = param.auctionIsClosed;
            obj.purchaseAgreementTemplateId = param.purchaseAgreementTemplateId;
            if(param.statusChangeReasons) obj.statusChangeReasons = param.statusChangeReasons.map(scr=>StatusChangeReason.mapFromDTO(scr));
            if(param.buyerUploadsRequired) {
                obj.buyerUploadsRequired=[];
                const burArray = JSON.parse(param.buyerUploadsRequired);
                burArray.forEach((bur:any) => {
                    obj.buyerUploadsRequired!.push(Object.assign(new BuyerUploadRequired(),bur));
                });
            }
            obj.showReserveMet = param.showReserveMet;
            obj.saleStatus = param.saleStatus ?? undefined; //public reads deliver null
            if(param.leads){
                obj.leads=[];
                const leadsArray = JSON.parse(param.leads);
                leadsArray.forEach((lead:any) => {
                    obj.leads!.push(Object.assign(new Lead(),lead));
                });
            }
        }
        catch(e)
        {
            console.log(e);
        }
    }
}

export class ChecklistItemStatus{
    checklistItemId!:number;
    checked:boolean = false;
    remarks?:string;
    timestamp!:Date;
    username!:string;
    history:ChecklistItemStatusHistory[]; //make sure its sorted latest first
    
    constructor(){
        this.history=[];
    }

    public static mapToDTO(param:ChecklistItemStatus) : ChecklistItemStatusDTO{
        
        let dto = new ChecklistItemStatusDTO();
        try
        {
            dto.checklistItemId = param.checklistItemId;
            dto.checked = param.checked;
            dto.remarks = param.remarks;
            if(param.timestamp) dto.timestamp = param.timestamp.toISOString();
            dto.username = param.username;
            if(param.history) dto.history = param.history.map(h=>ChecklistItemStatusHistory.mapToDTO(h));
        }
        catch(e)
        {
            console.log(e);
        }
        return dto;
    }
    public static mapFromDTO(param:ChecklistItemStatusDTO) : ChecklistItemStatus
    {
        
        let obj = new ChecklistItemStatus();
        try
        {
            obj.checklistItemId = param.checklistItemId;
            obj.checked = param.checked;
            obj.remarks = param.remarks;
            if(param.timestamp) obj.timestamp = new Date(param.timestamp);
            obj.username = param.username;
            if(param.history) {
                obj.history = param.history.map(h=>ChecklistItemStatusHistory.mapFromDTO(h));
                obj.history.sort((a, b) => (b.timestamp.getTime() - a.timestamp.getTime()));
            }
        }
        catch(e)
        {
            console.log(e);
        }
        return obj;
    }

    get clone(){
        return ChecklistItemStatus.mapFromDTO(ChecklistItemStatus.mapToDTO(this));
    }
}

export class ChecklistItemStatusHistory{
    checked:boolean = false;
    remarks?:string;
    timestamp!:Date;
    username!:string;

    public static mapToDTO(param:ChecklistItemStatusHistory) : ChecklistItemStatusHistoryDTO{
        
        let dto = new ChecklistItemStatusHistoryDTO();
        try
        {
            dto.checked = param.checked;
            dto.remarks = param.remarks;
            if(param.timestamp) dto.timestamp = param.timestamp.toISOString();
            dto.username = param.username;
        }
        catch(e)
        {
            console.log(e);
        }
        return dto;
    }
    public static mapFromDTO(param:ChecklistItemStatusHistoryDTO) : ChecklistItemStatusHistory
    {
        
        let obj = new ChecklistItemStatusHistory();
        try
        {
            obj.checked = param.checked;
            obj.remarks = param.remarks;
            if(param.timestamp) obj.timestamp = new Date(param.timestamp);
            obj.username = param.username;
        }
        catch(e)
        {
            console.log(e);
        }
        return obj;
    }
}

export class StatusChangeReason{
    status!:string;
    statusReason!:string;

    public static mapToDTO(param:StatusChangeReason) : StatusChangeReasonDTO{
        
        let dto = new StatusChangeReasonDTO();
        try
        {
            dto.status = param.status;
            dto.statusReason = param.statusReason;
        }
        catch(e)
        {
            console.log(e);
        }
        return dto;
    }
    public static mapFromDTO(param:StatusChangeReasonDTO) : StatusChangeReason
    {
        
        let obj = new StatusChangeReason();
        try
        {
            obj.status = param.status;
            obj.statusReason = param.statusReason;
        }
        catch(e)
        {
            console.log(e);
        }
        return obj;
    }
}

export class Lead{
    public id!:string; //random GUID at creation
    public name!:string;
    public phone?:string;
    public email?:string;
    public interestRating?:number; //0=low,10=medium,20=high,30=very high
    public internalContactId?:string;

    get displayInterestRating(){
        if(this.interestRating===0) return 'Low';
        if(this.interestRating===10) return 'Medium';
        if(this.interestRating===20) return 'High';
        if(this.interestRating===30) return 'Very High';
        return undefined;
    }

    public static ratings = [{id:0,name:'Low'},{id:10,name:'Medium'},{id:20,name:'High'},{id:30,name:'Very High'}];

    get clone(){
        return Object.assign(new Lead(),this);
        //if later on we have also non-primitive types in here do this: return structuredClone(Object.assign(new Lead(), this));
    }
}

export class BPSpreadData{
    public propertyExpenses?:PropertyExpense[];

    constructor(public auction:Auction){
    }

    //the reserve price set by the client not corrected for any buyer premium credits
    get clientReserve():number{
        if(this.auction.reservePrice){
            return this.auction.reservePrice;
        }
        return 0;
    }

    //the effective reserve price after applying any buyer premium credits
    get effectiveReserve():number{
        return this.clientReserve - this.reserveCredit;
    }

    //the difference between current price and client reserve
    get reserveDifference():number{
        if(!this.auction.currentPrice) return 0;
        return this.auction.currentPrice - this.clientReserve;
    }

    //the amount of buyer premium already applied towards reserve
    get reserveCredit():number{
        return this.auction.reserveCredit?this.auction.reserveCredit:0;
    }

    //the amount of additional buyer premium needed to reach reserve
    get reserveCreditNeeded():number{
        if(this.auction.reservePrice)
            return this.auction.reservePrice - this.minimumReserve + this.reserveCredit;
        return 0;   
    }

    //the minimum amount which needs to be reached to be able to use buyer premium towards reserve
    get minimumReserve():number{
        if(this.auction.reservePrice && this.auction.buyerPremium){
            return Math.round(this.auction.reservePrice/ (1 + this.auction.buyerPremium/100));
        }
        return 0;
    }

    //the spread between current price and minimum reserve
    get bpSpread():number{
        if(this.auction.currentPrice){
            return this.auction.currentPrice - this.minimumReserve;
        }
        return 0;
    }

    get currentBuyerPremium():number{
        if(this.auction.currentPrice && this.auction.buyerPremium){
            return Math.round(this.auction.currentPrice * (1 + this.auction.buyerPremium/100)) - this.clientReserve;
        }
        return 0;
    }

    get propertyExpensesTotal():number{
        return this.propertyExpenses?this.propertyExpenses?.reduce((accumulator,currentValue) => accumulator + currentValue.amount, 0):0;
    }

    get bpEffective(){
        if(this.auction.reserveMet && this.auction.highestBidAmount>0 && this.auction.currentPrice && this.auction.buyerPremium){
            if(this.reserveCredit>0) return (this.auction.currentPrice * (1+this.auction.buyerPremium/100)-this.clientReserve);
            else return this.auction.currentPrice*this.auction.buyerPremium/100;
        }
        else return 0;
    }

    get reserveMetNextBid(){
        if(!this.auction.currentPrice || !this.auction.increment) return 0;
        return this.auction.currentPrice + this.auction.increment;
    }
}

export class BuyerUploadRequired{
    public documentTypeId?:number;
    public documentTypeName?:string;
}
